import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { getWithRetry, isUnknownAnswer } from "../lib/fetchWithRetry";
import { FINISH_PARAM, finishMatchId, isPlannedMatch } from "../lib/plannedMatches";

/**
 * Issue #1150 (D225 item 6) — the `?finish=<matchId>` half of
 * `PerformanceTracking.jsx`: Add how it went (`PlannedMatchOverdueCard`) lands
 * here, and this opens the log flow's scorecard step for that planned match,
 * prefilled, headed "How did it go?".
 *
 * A sibling of `useEditDeepLink` and with the same contract: the id is read
 * once, on the mount the link caused, and the param is stripped at once so a
 * bookmark never re-opens it. It differs in what it reads: `GET /matches`
 * lists played matches only (#1148), so a planned match is not in that list
 * and is fetched by id.
 *
 * `openFinish(match)` is `PerformanceTracking`'s own (`openEdit`: it owns the
 * form state). An id that names no match of the player's (404/403), or a match
 * that is no longer planned (finished in another tab), opens nothing and the
 * player lands on the ordinary list. A read that never got an answer is NOT
 * dropped: `finishFailed` is true so the page can say so (D57).
 *
 * @param {(match: object) => void} openFinish
 * @param {boolean} [enabled=true] — false (the scorecard flag is off): do nothing at all.
 * @returns {{ finishingId: string|null, finishMatch: object|null, finishFailed: boolean,
 *   clearFinish: () => void, leaveFinish: () => void }}
 *   `clearFinish` forgets the match (a close, or the saved step's exit, which
 *   navigates itself); `leaveFinish` also returns to the screen the card was on.
 */
export function useFinishPlannedMatch(openFinish, enabled = true) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sp, setSp] = useSearchParams();
  // Lazy: read on the mount the link caused, never re-derived (the param is gone by then).
  // D217: finish is a new-look flow. The caller passes `enabled` (its own
  // `newUi("log-scorecard")`, the flag of the scorecard step this opens); with it
  // off a `?finish=` does nothing, so the old dialog can never be opened on a
  // planned match. The flag is read by the caller, not here, so this hook has no
  // gate shape for `flagSafeLinks` to scan.
  const [pendingId] = useState(() => (enabled ? finishMatchId(sp) : null));
  const fromRef = useRef(location?.state?.from || null);
  // The page's latest `openFinish`, read when the fetch answers.
  const openRef = useRef(openFinish);
  openRef.current = openFinish;
  const [finishMatch, setFinishMatch] = useState(null);
  const [finishFailed, setFinishFailed] = useState(false);

  useEffect(() => {
    if (!pendingId || !finishMatchId(sp)) return;
    const next = new URLSearchParams(sp);
    next.delete(FINISH_PARAM);
    setSp(next, { replace: true });
    // Mount-only: a one-time strip, not a rule for every `sp` change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!pendingId) return undefined;
    let alive = true;
    getWithRetry(`/matches/${encodeURIComponent(pendingId)}`)
      .then((r) => {
        if (!alive) return;
        const m = r?.data;
        if (!isPlannedMatch(m)) return;
        setFinishMatch(m);
        openRef.current(m);
      })
      .catch((e) => {
        if (!alive) return;
        if (isUnknownAnswer(e)) {
          console.error("useFinishPlannedMatch: failed to load the planned match", e);
          setFinishFailed(true);
        }
      });
    return () => {
      alive = false;
    };
    // Mount-only, like the strip above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFinish = useCallback(() => setFinishMatch(null), []);
  const leaveFinish = useCallback(() => {
    setFinishMatch(null);
    navigate(fromRef.current || "/dashboard");
  }, [navigate]);

  return {
    finishingId: finishMatch ? String(finishMatch.match_id) : null,
    finishMatch,
    finishFailed,
    clearFinish,
    leaveFinish,
  };
}

export default useFinishPlannedMatch;
